import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import type { SummaryImage } from '@/lib/media/summary-images';
import { buildTweetPrompt, DEFAULT_TWEET_SUMMARY_INSTRUCTION } from '@/lib/tweet/prompts';
import type { Tweet as TweetType } from '@/types/tweet';

import type { InlineTweetReference, SummaryContextSnapshot } from './types';

export function toIsoString(value: Date | null | undefined): string | undefined {
  if (!value) {
    return;
  }
  return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
}

type TweetMediaItem = NonNullable<TweetType['mediaItems']>[number];
type TweetMediaVariant = NonNullable<NonNullable<TweetMediaItem['variants']>>[number];
type TweetAuthorInfo = NonNullable<TweetType['author']>;

function serializeMediaVariant(variant: TweetMediaVariant) {
  return {
    ...(typeof variant.bitRate === 'number' ? { bitRate: variant.bitRate } : {}),
    contentType: variant.contentType,
    url: variant.url,
  };
}

function serializeMediaItem(item: TweetMediaItem | undefined | null) {
  if (!item) {
    return;
  }

  return {
    mediaKey: item.mediaKey,
    type: item.type,
    ...(item.url ? { url: item.url } : {}),
    ...(item.previewImageUrl ? { previewImageUrl: item.previewImageUrl } : {}),
    ...(typeof item.width === 'number' ? { width: item.width } : {}),
    ...(typeof item.height === 'number' ? { height: item.height } : {}),
    ...(item.altText ? { altText: item.altText } : {}),
    ...(typeof item.durationMs === 'number' ? { durationMs: item.durationMs } : {}),
    variants: Array.isArray(item.variants) ? item.variants.map((variant) => serializeMediaVariant(variant)) : [],
  } satisfies Record<string, unknown>;
}

function serializeAuthor(author: TweetAuthorInfo | null | undefined) {
  if (!author) {
    return;
  }

  const createdAt = toIsoString(author.createdAt);

  return {
    id: author.id,
    username: author.username,
    name: author.name,
    ...(createdAt ? { createdAt } : {}),
    verifiedType: author.verifiedType,
    ...(typeof author.verified === 'boolean' ? { verified: author.verified } : {}),
    ...(author.description ? { description: author.description } : {}),
    ...(author.publicMetrics ? { publicMetrics: author.publicMetrics } : {}),
    ...(author.affiliations ? { affiliations: author.affiliations } : {}),
    ...(author.primaryAffiliation ? { primaryAffiliation: author.primaryAffiliation } : {}),
  } satisfies Record<string, unknown>;
}

export function serializeTweetForContext(
  tweet: TweetType | null | undefined,
  visited: Set<string> = new Set()
): Record<string, unknown> | undefined {
  if (!tweet) {
    return;
  }

  const identifier = tweet.id ?? `anonymous-${visited.size}`;
  if (visited.has(identifier)) {
    return { id: tweet.id, note: 'reference already serialized' };
  }

  visited.add(identifier);

  const mediaItems = Array.isArray(tweet.mediaItems)
    ? tweet.mediaItems
        .map((item) => serializeMediaItem(item))
        .filter((item): item is NonNullable<ReturnType<typeof serializeMediaItem>> => item !== undefined)
    : [];

  const author = serializeAuthor(tweet.author);
  const quotedTweet = serializeTweetForContext(tweet.quotedTweet, visited);
  const retweetedTweet = serializeTweetForContext(tweet.retweetedTweet, visited);
  const createdAt = toIsoString(tweet.createdAt);
  const updatedAt = toIsoString(tweet.updatedAt ?? undefined);

  return {
    id: tweet.id,
    text: tweet.text,
    authorId: tweet.authorId,
    ...(author ? { author } : {}),
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    ...(tweet.conversationId ? { conversationId: tweet.conversationId } : {}),
    ...(tweet.inReplyToUserId ? { inReplyToUserId: tweet.inReplyToUserId } : {}),
    ...(tweet.lang ? { lang: tweet.lang } : {}),
    ...(tweet.noteTweet ? { noteTweet: { text: tweet.noteTweet.text } } : {}),
    metrics: {
      likeCount: tweet.likeCount,
      retweetCount: tweet.retweetCount,
      replyCount: tweet.replyCount,
      quoteCount: tweet.quoteCount,
      ...(typeof tweet.bookmarkCount === 'number' ? { bookmarkCount: tweet.bookmarkCount } : {}),
      ...(typeof tweet.impressionCount === 'number' ? { impressionCount: tweet.impressionCount } : {}),
    },
    ...(tweet.entities ? { entities: tweet.entities } : {}),
    ...(tweet.attachments ? { attachments: tweet.attachments } : {}),
    mediaItems,
    ...(tweet.referencedTweets ? { referencedTweets: tweet.referencedTweets } : {}),
    ...(quotedTweet ? { quotedTweet } : {}),
    ...(retweetedTweet ? { retweetedTweet } : {}),
    ...(tweet.metadata ? { metadata: tweet.metadata } : {}),
    ...(tweet.mediaMetadata ? { mediaMetadata: tweet.mediaMetadata } : {}),
    ...(tweet.contextAnnotations ? { contextAnnotations: tweet.contextAnnotations } : {}),
    ...(typeof tweet.possiblySensitive === 'boolean' ? { possiblySensitive: tweet.possiblySensitive } : {}),
    ...(tweet.replySettings ? { replySettings: tweet.replySettings } : {}),
    ...(typeof tweet.isRetweet === 'boolean' ? { isRetweet: tweet.isRetweet } : {}),
    ...(typeof tweet.isPinned === 'boolean' ? { isPinned: tweet.isPinned } : {}),
    ...(typeof tweet.isLiked === 'boolean' ? { isLiked: tweet.isLiked } : {}),
    ...(typeof tweet.isRetweeted === 'boolean' ? { isRetweeted: tweet.isRetweeted } : {}),
    ...(typeof tweet.isBookmarked === 'boolean' ? { isBookmarked: tweet.isBookmarked } : {}),
    ...(typeof tweet.relevanceScore === 'number' ? { relevanceScore: tweet.relevanceScore } : {}),
    ...(tweet.authorAffiliations ? { authorAffiliations: tweet.authorAffiliations } : {}),
    ...(tweet.authorPrimaryAffiliation ? { authorPrimaryAffiliation: tweet.authorPrimaryAffiliation } : {}),
  };
}

export function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, Math.max(0, maxLength - 1))}…`;
}

export function buildSummaryPromptText({
  tweet,
  summaryImages,
  includeImages,
  customPrompt,
}: {
  tweet: TweetType;
  summaryImages: SummaryImage[];
  includeImages: boolean;
  customPrompt: string;
}): string {
  const rawText = tweet.noteTweet?.text?.trim() || tweet.text?.trim() || '';
  const createdAtIso = tweet.createdAt.toISOString();

  const authorUsername = tweet.author?.username ?? 'unknown';
  const authorName = tweet.author?.name ?? authorUsername;

  const normalizedCustomPrompt = customPrompt.length > 0 ? customPrompt : DEFAULT_TWEET_SUMMARY_INSTRUCTION;

  return buildTweetPrompt({
    authorUsername,
    authorName,
    createdAtIso,
    tweetText: rawText,
    images: includeImages ? summaryImages : [],
    customPrompt: normalizedCustomPrompt,
    fallbackInstruction: DEFAULT_TWEET_SUMMARY_INSTRUCTION,
  });
}

export function createChatInlineReferences(references: InlineTweetReference[]) {
  return references.map((reference) => ({
    tweetId: reference.tweetId,
    url: reference.url,
    username: reference.username,
  }));
}

export function createSummaryContextSections({
  snapshot,
  metadata,
}: {
  snapshot: SummaryContextSnapshot;
  metadata: Record<string, unknown>;
}): string {
  const promptSection =
    snapshot.prompt.length > 0 ? ['```markdown', snapshot.prompt, '```'].join('\n') : '_Prompt unavailable_';
  const metadataSection = ['```json', JSON.stringify(metadata, undefined, 2), '```'].join('\n');
  const sections: string[] = [
    '#### Prompt used to generate summary',
    promptSection,
    '#### Summary metadata',
    metadataSection,
  ];
  return sections.join('\n\n');
}

export function formatCount(value: number | null | undefined): string {
  if (typeof value !== 'number') {
    return '0';
  }
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function dedupeStrings(values: (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    if (!value) continue;
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    seen.add(trimmed.toLowerCase());
  }
  return [...seen];
}

export function createExplainContextPayload({
  tweet,
  inlineTweetUrls,
  summaryImages,
  summaryPreview,
}: {
  tweet: TweetType;
  inlineTweetUrls: InlineTweetReference[];
  summaryImages: SummaryImage[];
  summaryPreview: string | null;
}): UniversalChatContextPayload {
  return {
    source: `tweet-explain-${tweet.id}`,
    title: `Tweet ${tweet.id} context`,
    summary: summaryPreview ?? undefined,
    data: {
      tweet: serializeTweetForContext(tweet),
      inlineTweetReferences: createChatInlineReferences(inlineTweetUrls),
      mediaSummary: summaryImages.map((image) => ({
        url: image.url,
        ...(image.altText ? { altText: image.altText } : {}),
      })),
    },
  } satisfies UniversalChatContextPayload;
}

export function createExplainMessages(tweetId: string, timestamp: Date): [Message, Message] {
  const systemMessage: Message = {
    id: `${tweetId}-explain-system-${timestamp.getTime()}`,
    role: 'system',
    content: [
      'You are helping the user understand the tweet context above.',
      'Provide a clear, reader-friendly explanation that covers the main tweet, referenced tweets, and attached media.',
      'Highlight why the tweet matters and clarify any imagery or inline references when relevant.',
    ].join('\n'),
    timestamp,
    visibility: 'hidden',
  };

  const userMessage: Message = {
    id: `${tweetId}-explain-user-${timestamp.getTime()}-request`,
    role: 'user',
    content: 'Please explain this tweet using the provided context.',
    timestamp: new Date(timestamp.getTime() + 1),
  };

  return [systemMessage, userMessage];
}
